class BlogArchives {
  constructor(config) {
    this.config = config;
    this.allArticles = [];
    this.filteredArticles = [];
    this.params = new URLSearchParams(window.location.search);
    this.year = this.params.get('year');
    this.month = this.params.get('month');
    this.currentPage = Math.max(1, parseInt(this.params.get('page') || '1', 10));
    this.cacheKey = `blog-archives-v1-${config.blogHandles.join('-')}`;
  }

  async init() {
    this.toggleSections();
    this.renderBreadcrumb();

    try {
      this.allArticles = await this.fetchAllArticles();
      this.allArticles.sort(
        (a, b) => new Date(b.published_at) - new Date(a.published_at)
      );
      this.renderTitle();
      this.renderArchiveNav();
      this.applyFilter();
    } catch (error) {
      console.error('BlogArchives:', error);
      document.getElementById('archive-nav').innerHTML =
        '<p>記事の読み込みに失敗しました。</p>';
      document.getElementById('top_post_list').innerHTML = '';
    }
  }

  async fetchAllArticles() {
    const cached = sessionStorage.getItem(this.cacheKey);
    if (cached) {
      return JSON.parse(cached);
    }

    const results = [];
    for (const handle of this.config.blogHandles) {
      let page = 1;
      let totalPages = 1;

      try {
        do {
          const response = await fetch(
            `/blogs/${handle}?view=archive-json&page=${page}`
          );
          if (!response.ok) {
            throw new Error(`Failed to fetch blog: ${handle}`);
          }
          const data = await response.json();
          totalPages = data.pagination.total_pages;

          data.articles.forEach((article) => {
            article.blog_title = data.blog.title;
            article.blog_url = data.blog.url;
          });

          results.push(...data.articles);
          page += 1;
        } while (page <= totalPages);
      } catch (error) {
        // 未作成・非公開のブログはスキップ（1件の失敗で全体を落とさない）
        console.warn('BlogArchives:', error.message || error);
      }
    }

    sessionStorage.setItem(this.cacheKey, JSON.stringify(results));
    return results;
  }

  buildArchiveMap() {
    const map = {};
    this.allArticles.forEach(({ year, month }) => {
      if (!map[year]) {
        map[year] = new Set();
      }
      map[year].add(Number(month));
    });
    return map;
  }

  toggleSections() {
    const isArchive = Boolean(this.year);

    document.getElementById('archive-main-title').hidden = isArchive;
    document.getElementById('archive_list').hidden = !isArchive;
  }

  renderBreadcrumb() {
    const breadcrumbEl = document.getElementById('archive-breadcrumb');
    const items = [
      {
        label: 'TOP',
        href: this.config.shopUrl,
      },
    ];

    if (this.year && this.month) {
      items.push(
        {
          label: this.config.breadcrumbLabel,
          href: this.config.archiveBase,
        },
        {
          label: `${this.year}年`,
          href: this.buildArchiveUrl({ year: this.year, month: null, page: null }),
        },
        {
          label: `${Number(this.month)}月`,
        }
      );
    } else if (this.year) {
      items.push(
        {
          label: this.config.breadcrumbLabel,
          href: this.config.archiveBase,
        },
        {
          label: `${this.year}年`,
        }
      );
    } else {
      items.push({
        label: this.config.breadcrumbLabel,
      });
    }

    breadcrumbEl.innerHTML = items
      .map((item) => {
        const content = item.href
          ? `<a href="${this.escapeHtml(item.href)}"><span>${this.escapeHtml(item.label)}</span></a>`
          : `<span>${this.escapeHtml(item.label)}</span>`;
        return `<li>${content}</li>`;
      })
      .join('');
  }

  renderTitle() {
    const titleEl = document.getElementById('archive-title');
    if (!this.year) {
      titleEl.textContent = 'バックナンバー';
      return;
    }

    let title = `バックナンバー\u00a0\u00a0:\u00a0\u00a0${this.year}年`;
    if (this.month) {
      title += `${Number(this.month)}月`;
    }
    titleEl.textContent = title;
  }

  renderArchiveNav() {
    const map = this.buildArchiveMap();
    const endYear = new Date().getFullYear();
    const navEl = document.getElementById('archive-nav');
    let html = '';

    for (let year = this.config.startYear; year <= endYear; year += 1) {
      const months = map[year];
      const yearClass = months ? 'archive_year' : 'noarchive_year';
      const yearContent = months
        ? `<a href="${this.buildArchiveUrl({ year })}">${year}年</a>`
        : `${year}年`;

      html += '<div class="clearfix mb10"><ul>';
      html += `<li class="${yearClass}">${yearContent}</li>`;

      for (let month = 1; month <= 12; month += 1) {
        const hasArticles = months?.has(month);
        html += '<li class="archive_month">';
        if (hasArticles) {
          html += `<a href="${this.buildArchiveUrl({ year, month })}">${month}月</a>`;
        } else {
          html += `${month}月`;
        }
        html += '</li>';
      }

      html += '</ul></div>';
    }

    navEl.innerHTML = html;
  }

  applyFilter() {
    this.filteredArticles = this.allArticles.filter((article) => {
      const matchYear = !this.year || String(article.year) === this.year;
      const matchMonth =
        !this.month || Number(article.month) === Number(this.month);
      return matchYear && matchMonth;
    });

    const totalPages = Math.ceil(
      this.filteredArticles.length / this.config.articlesPerPage
    );
    if (this.currentPage > totalPages && totalPages > 0) {
      this.currentPage = totalPages;
    }

    this.renderArticles();
    this.renderPagination();
  }

  renderArticles() {
    const container = document.getElementById('top_post_list');
    const start = (this.currentPage - 1) * this.config.articlesPerPage;
    const pageArticles = this.filteredArticles.slice(
      start,
      start + this.config.articlesPerPage
    );

    if (pageArticles.length === 0) {
      container.innerHTML = '<p>記事が見つかりませんでした。</p>';
      return;
    }

    container.innerHTML = pageArticles
      .map((article) => this.renderArticleCard(article))
      .join('');
  }

  renderArticleCard(article) {
    const dateDisplay = article.published_at.replace(/-/g, '/');
    const imageHtml = article.image
      ? `<img src="${this.escapeHtml(article.image.src)}" alt="${this.escapeHtml(article.image.alt)}">`
      : '<div>No Image</div>';
    const thumbnailClass = article.image ? '' : ' class="post_list_thumbnail_image"';

    return `
      <article class="top_post">
        <figure class="post_list_thumbnail">
          <a href="${this.escapeHtml(article.url)}"${thumbnailClass}>
            ${imageHtml}
          </a>
        </figure>
        <div class="post_list_box">
          <div class="info_date_title_cate">
            <time class="sub_info_date" datetime="${this.escapeHtml(article.published_at)}">
              <a href="/pages/blog?year=${article.year}&month=${article.month}" title="${this.escapeHtml(article.title)}">
                ${dateDisplay}
              </a>
            </time>
            <div class="title_cate">
              <h2 class="post_title">
                <a href="${this.escapeHtml(article.url)}">${this.escapeHtml(article.title)}</a>
              </h2>
              <div class="sub_info_cate">
                <a href="${this.escapeHtml(article.blog_url)}">${this.escapeHtml(article.blog_title)}</a>
              </div>
            </div>
          </div>
          <p class="content">
            <a href="${this.escapeHtml(article.url)}">${this.escapeHtml(article.excerpt)}</a>
          </p>
          <div class="more">
            <a href="${this.escapeHtml(article.url)}" class="button next-arrow">詳しく見る</a>
          </div>
        </div>
      </article>
    `;
  }

  renderPagination() {
    const totalPages = Math.ceil(
      this.filteredArticles.length / this.config.articlesPerPage
    );
    const html =
      totalPages <= 1 ? '' : this.buildPaginationHtml(totalPages);

    document.getElementById('archive-pagination-top').innerHTML = html;
    document.getElementById('archive-pagination-bottom').innerHTML = html;
  }

  buildPaginationHtml(totalPages) {
    const current = this.currentPage;
    const range = 1;
    let html = '<div class="pagination">';

    if (current > 1) {
      html += `<a href="${this.buildArchiveUrl({ page: current - 1 })}" class="arrow_left"></a>`;
    }

    if (current > 2 && current > range + 1) {
      html += `<a href="${this.buildArchiveUrl({ page: 1 })}" class="inactive">1</a>`;
      html += '<span>...</span>';
    }

    const minShow = current - range;
    const maxShow = current + range;

    for (let i = 1; i <= totalPages; i += 1) {
      if (i >= minShow && i <= maxShow) {
        if (i === current) {
          html += `<span class="current">${i}</span>`;
        } else {
          html += `<a href="${this.buildArchiveUrl({ page: i })}" class="inactive">${i}</a>`;
        }
      }
    }

    if (current < totalPages - range && current + range - 1 < totalPages) {
      html += '<span>...</span>';
      html += `<a href="${this.buildArchiveUrl({ page: totalPages })}" class="inactive">${totalPages}</a>`;
    }

    if (current < totalPages) {
      html += `<a href="${this.buildArchiveUrl({ page: current + 1 })}" class="arrow_right"></a>`;
    }

    html += '</div>';
    return html;
  }

  buildArchiveUrl({ year, month, page } = {}) {
    const params = new URLSearchParams();

    const targetYear = year !== undefined ? year : this.year;
    const targetMonth = month !== undefined ? month : this.month;
    const targetPage = page !== undefined ? page : null;

    if (targetYear) {
      params.set('year', targetYear);
    }
    if (targetMonth) {
      params.set('month', targetMonth);
    }
    if (targetPage && targetPage > 1) {
      params.set('page', targetPage);
    }

    const query = params.toString();
    return query ? `${this.config.archiveBase}?${query}` : this.config.archiveBase;
  }

  escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (!window.BlogArchivesConfig) return;
  new BlogArchives(window.BlogArchivesConfig).init();
});
